import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Progress,
  Row,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { DownloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { adminApi } from '@services/api';
import type { DailyReportAdsResponse, DailyReportDay, DailyReportResponse } from '@/types';
import { reportLoadErrorMessage } from '@/utils/apiError';
import { REPORT_MAX_DAYS, disabledBeyondReportCap, isWithinReportCap } from '@/utils/reportRange';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

interface FunnelRow {
  day: string;
  firstOpens: number;
  tutorialBegin: number;
  tutorialComplete: number;
  firstMatch: number;
  d1Retained: number;
}

interface AbandonmentRow {
  matchType: string;
  abandonReason: string | null;
  overBucket: string;
  matches: number;
}

interface FtueRow {
  step: string;
  entered: number;
  medianPreviousStepSeconds: number;
}

const BUCKET_ORDER = ['0-25%', '25-50%', '50-75%', '75-100%', 'unknown'];

const pct = (n: number, d: number) => (d > 0 ? Math.round((n / d) * 100) : 0);

// ─── Daily report, sheet view ───────────────────────────────────────

/** The endpoint's window limit; a wider range is refused with a 400. */
const SHEET_MAX_DAYS = REPORT_MAX_DAYS;

type SheetValueKind = 'count' | 'ratio' | 'duration' | 'money';

interface SheetMetric {
  key: string;
  /** Row number in the team's spreadsheet; '—' when the sheet has no row for it. */
  sheetRow: string;
  label: string;
  kind: SheetValueKind;
  /** ISO code, for `money` rows only. */
  currency?: string;
  hint?: string;
  value: (d: DailyReportDay) => number | null;
}

interface SheetRow {
  key: string;
  sheetRow: string;
  label: string;
  hint?: string;
  /** Display text per day, in `days` order. */
  display: string[];
  /** Spreadsheet-friendly text per day: no grouping or symbols, '' for no value. */
  csv: string[];
}

const PLAYER_BUCKETS = ['0', '1', '2', '3', '4', '5+'] as const;

const SHEET_METRICS: SheetMetric[] = [
  {
    key: 'newUsers',
    sheetRow: '2',
    label: 'First time users (D0)',
    kind: 'count',
    hint: 'Accounts created that IST day, excluding merged accounts.',
    value: (d) => d.newUsers,
  },
  {
    key: 'ftueCompletionsD0',
    sheetRow: '3',
    label: 'FTUE completions on D0',
    kind: 'count',
    hint: "Of that day's new users, how many completed the FTUE on the same IST day.",
    value: (d) => d.ftueCompletionsD0,
  },
  {
    key: 'ftueCompletions',
    sheetRow: '4',
    label: 'FTUE completions',
    kind: 'count',
    hint: 'Everyone who completed the FTUE that day, whenever they signed up.',
    value: (d) => d.ftueCompletions,
  },
  {
    key: 'quickPlayStarted',
    sheetRow: '6',
    label: 'Quick Play started',
    kind: 'count',
    value: (d) => d.quickPlayStarted,
  },
  {
    key: 'quickPlayCompleted',
    sheetRow: '7',
    label: 'Quick Play completed',
    kind: 'count',
    value: (d) => d.quickPlayCompleted,
  },
  {
    key: 'customMatchStarted',
    sheetRow: '11',
    label: 'Custom Match started',
    kind: 'count',
    value: (d) => d.customMatchStarted,
  },
  {
    key: 'customMatchCompleted',
    sheetRow: '12',
    label: 'Custom Match completed',
    kind: 'count',
    value: (d) => d.customMatchCompleted,
  },
  {
    key: 'blitzStarted',
    sheetRow: '—',
    label: 'Blitz started',
    kind: 'count',
    value: (d) => d.blitzStarted,
  },
  {
    key: 'blitzCompleted',
    sheetRow: '—',
    label: 'Blitz completed',
    kind: 'count',
    value: (d) => d.blitzCompleted,
  },
  {
    key: 'matchesStarted',
    sheetRow: '15',
    label: 'Matches started',
    kind: 'count',
    hint: 'All match types, by the IST day the match was created.',
    value: (d) => d.matchesStarted,
  },
  {
    key: 'matchesCompleted',
    sheetRow: '16',
    label: 'Matches completed',
    kind: 'count',
    hint: 'All match types, by the IST day the match completed.',
    value: (d) => d.matchesCompleted,
  },
  {
    key: 'd0PlayersStarted',
    sheetRow: '17',
    label: 'D0 players who started a match',
    kind: 'count',
    hint: "Of that day's new users, how many started at least one match on the same IST day.",
    value: (d) => d.d0PlayersStarted ?? null,
  },
  {
    key: 'd0PlayersCompleted',
    sheetRow: '18',
    label: 'D0 players who completed a match',
    kind: 'count',
    hint: "Of that day's new users, how many completed at least one match on the same IST day. A match that ends after IST midnight does not count.",
    value: (d) => d.d0PlayersCompleted ?? null,
  },
  {
    key: 'dau',
    sheetRow: '—',
    label: 'DAU',
    kind: 'count',
    hint: 'Distinct users with any recorded activity that IST day.',
    value: (d) => d.dau,
  },
  {
    key: 'matchesStartedPerDau',
    sheetRow: '21',
    label: 'Matches started per DAU',
    kind: 'ratio',
    value: (d) => d.matchesStartedPerDau,
  },
  {
    key: 'matchesCompletedPerDau',
    sheetRow: '22 / 30',
    label: 'Matches completed per DAU',
    kind: 'ratio',
    value: (d) => d.matchesCompletedPerDau,
  },
  {
    key: 'd0MatchesStartedPerUser',
    sheetRow: '24',
    label: 'D0 matches started per new user',
    kind: 'ratio',
    hint: "Matches that day's new users started on the same IST day, divided by new users.",
    value: (d) => d.d0MatchesStartedPerUser ?? null,
  },
  {
    key: 'd0MatchesCompletedPerUser',
    sheetRow: '25',
    label: 'D0 matches completed per new user',
    kind: 'ratio',
    hint: "Matches that day's new users completed on the same IST day, divided by new users.",
    value: (d) => d.d0MatchesCompletedPerUser ?? null,
  },
  {
    key: 'startedAndCompletedSameDay',
    sheetRow: '31',
    label: 'Started and completed same day',
    kind: 'count',
    hint: 'Matches created that IST day that also completed that IST day.',
    value: (d) => d.startedAndCompletedSameDay,
  },
  ...PLAYER_BUCKETS.map(
    (bucket): SheetMetric => ({
      key: `players-${bucket}`,
      sheetRow: '35–37',
      label: `Players with ${bucket} completed ${bucket === '1' ? 'match' : 'matches'}`,
      kind: 'count',
      hint:
        bucket === '0'
          ? 'DAU minus the players who completed at least one match that day.'
          : 'Distinct players, by the number of matches they completed that day.',
      value: (d) => d.playersByCompletedMatches[bucket],
    })
  ),
  {
    key: 'sessions',
    sheetRow: '38',
    label: 'Sessions',
    kind: 'count',
    hint: 'Sessions that started that IST day and have ended.',
    value: (d) => d.sessions,
  },
  {
    key: 'sessionMedianSeconds',
    sheetRow: '39',
    label: 'Session length (median)',
    kind: 'duration',
    value: (d) => d.sessionMedianSeconds,
  },
  {
    key: 'sessionP90Seconds',
    sheetRow: '39',
    label: 'Session length (p90)',
    kind: 'duration',
    value: (d) => d.sessionP90Seconds,
  },
];

const PAYERS_METRIC: SheetMetric = {
  key: 'payers',
  sheetRow: '46',
  label: 'Daily payers',
  kind: 'count',
  hint: 'Distinct users with a real completed purchase that IST day.',
  value: (d) => d.payers,
};

const REVENUE_HINT =
  'Completed, real purchases only (no sandbox, tester or editor). Each currency is its own row — they are never added together.';

const ADS_SOURCE_HINT =
  "From the game's GA4 events in BigQuery (rebuilt every 4 hours), in USD. The latest day or two read — until they are built.";

/** Rows 43-44 from the BigQuery answer; when it is missing every day reads —. */
const adsMetrics = (ads: DailyReportAdsResponse | null): SheetMetric[] => {
  const byDay = new Map((ads?.days ?? []).map((d) => [d.day, d]));
  const hint =
    ads && !ads.available
      ? 'BigQuery could not answer just now; reload in a few minutes.'
      : ADS_SOURCE_HINT;
  return [
    {
      key: 'adRevenueUsd',
      sheetRow: '43',
      label: 'Ads revenue (USD)',
      kind: 'money',
      currency: 'USD',
      hint,
      value: (d) => byDay.get(d.day)?.adRevenueUsd ?? null,
    },
    {
      key: 'totalRevenueUsd',
      sheetRow: '44',
      label: 'Total revenue (USD, ads + IAP)',
      kind: 'money',
      currency: 'USD',
      hint: `${hint} IAP here is GA4's gross figure, so it differs from the store revenue in row 45.`,
      value: (d) => byDay.get(d.day)?.totalRevenueUsd ?? null,
    },
  ];
};

/** "4m 12s"; an hour or more reads "1h 5m". */
const formatDuration = (seconds: number) => {
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m ${s % 60}s`;
};

const formatRevenue = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(amount);
  } catch {
    // Not a currency code Intl accepts (e.g. UNKNOWN) — it throws a RangeError.
    return amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
};

const displayCell = (metric: SheetMetric, v: number | null) => {
  if (v === null) return '—';
  switch (metric.kind) {
    case 'ratio':
      return v.toFixed(2);
    case 'duration':
      return formatDuration(v);
    case 'money':
      return formatRevenue(v, metric.currency ?? '');
    default:
      return v.toLocaleString('en-IN');
  }
};

const csvCell = (metric: SheetMetric, v: number | null) => {
  if (v === null) return '';
  switch (metric.kind) {
    case 'ratio':
    case 'money':
      return v.toFixed(2);
    case 'duration':
      return formatDuration(v);
    default:
      return String(v);
  }
};

/** One row per metric, one value per day — the spreadsheet's layout. */
const buildSheetRows = (days: DailyReportDay[], ads: DailyReportAdsResponse | null): SheetRow[] => {
  const currencies = Array.from(
    new Set(days.flatMap((d) => d.iapRevenue.map((r) => r.currency)))
  ).sort();
  const revenue: SheetMetric[] =
    currencies.length > 0
      ? currencies.map((currency) => ({
          key: `iap-${currency}`,
          sheetRow: '45',
          label: `IAP revenue (${currency})`,
          kind: 'money',
          currency,
          hint: REVENUE_HINT,
          // Days are zero-filled: no entry for a currency means none that day.
          value: (d) => d.iapRevenue.find((r) => r.currency === currency)?.amount ?? 0,
        }))
      : [
          {
            key: 'iap-none',
            sheetRow: '45',
            label: 'IAP revenue',
            kind: 'count',
            hint: `${REVENUE_HINT} There was none in this range.`,
            value: () => 0,
          },
        ];
  return [...SHEET_METRICS, ...adsMetrics(ads), ...revenue, PAYERS_METRIC].map((metric) => {
    const values = days.map(metric.value);
    return {
      key: metric.key,
      sheetRow: metric.sheetRow,
      label: metric.label,
      hint: metric.hint,
      display: values.map((v) => displayCell(metric, v)),
      csv: values.map((v) => csvCell(metric, v)),
    };
  });
};

const csvEscape = (s: string) => (/[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);

const downloadSheetCsv = (days: DailyReportDay[], rows: SheetRow[]) => {
  const lines = [
    ['Sheet row', 'Metric', ...days.map((d) => d.day)],
    ...rows.map((r) => [r.sheetRow, r.label, ...r.csv]),
  ].map((cols) => cols.map(csvEscape).join(','));
  // The BOM makes Excel read the dashes in the row column as UTF-8.
  const blob = new Blob([`\uFEFF${lines.join('\r\n')}\r\n`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `daily-report-${days[0].day}-to-${days[days.length - 1].day}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * The daily-report reads: acquisition funnel, abandonment by over, FTUE
 * drop-off.
 *
 * Each card states the caveat that makes its number readable rather than
 * leaving it to be rediscovered — D1 only counts from when activity tracking
 * started, and the abandonment distribution is only meaningful once you know
 * how much of it came from the sweeper rather than from players quitting.
 */
export const DailyReportPanel: React.FC = () => {
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(29, 'day').startOf('day'),
    dayjs().endOf('day'),
  ]);
  const [funnel, setFunnel] = useState<FunnelRow[]>([]);
  const [abandon, setAbandon] = useState<AbandonmentRow[]>([]);
  const [ftue, setFtue] = useState<FtueRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // The sheet view loads on its own so a failure there never blanks the
  // cards above, and theirs never blanks it.
  const [report, setReport] = useState<DailyReportResponse | null>(null);
  // Rows 43-44 come from BigQuery; null (failed or not loaded) shows them as —.
  const [ads, setAds] = useState<DailyReportAdsResponse | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportLoading, setReportLoading] = useState(true);
  const reportRequest = useRef(0);

  const params = useMemo(
    () => ({
      from: range[0].format('YYYY-MM-DD'),
      to: range[1].add(1, 'day').format('YYYY-MM-DD'),
    }),
    [range]
  );
  const sheetTooWide = dayjs(params.to).diff(dayjs(params.from), 'day') > SHEET_MAX_DAYS;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [f, a, t] = await Promise.all([
        adminApi.getAcquisitionFunnel(params),
        adminApi.getAbandonmentBreakdown(params),
        adminApi.getFtueFunnel(params),
      ]);
      setFunnel(f.data ?? []);
      setAbandon(a.data ?? []);
      setFtue(t.data ?? []);
      setError(null);
    } catch (e) {
      // Names the three cards it covers: the sheet view below loads on its own
      // and may be fine while these failed. A 400 shows the server's reason.
      setError(reportLoadErrorMessage(e, 'Could not load the funnel, abandonment and FTUE cards'));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const fetchReport = useCallback(async () => {
    // Only the latest range may write state: a slow response for a range the
    // user has already moved away from must not overwrite the current one.
    const request = ++reportRequest.current;
    if (sheetTooWide) {
      setReport(null);
      setAds(null);
      setReportError(null);
      setReportLoading(false);
      return;
    }
    setReportLoading(true);
    try {
      const [res, adsRes] = await Promise.all([
        adminApi.getDailyReport(params),
        // The ad rows must never take the sheet down with them.
        adminApi.getDailyReportAds(params).catch(() => null),
      ]);
      if (request !== reportRequest.current) return;
      setReport(res.data);
      setAds(adsRes?.data ?? null);
      setReportError(null);
    } catch (e) {
      if (request !== reportRequest.current) return;
      setReport(null);
      setAds(null);
      setReportError(reportLoadErrorMessage(e, 'Could not load the daily report sheet'));
    } finally {
      if (request === reportRequest.current) setReportLoading(false);
    }
  }, [params, sheetTooWide]);

  useEffect(() => {
    void fetchReport();
  }, [fetchReport]);

  const sheetDays = useMemo(() => report?.days ?? [], [report]);
  const sheetRows = useMemo(() => buildSheetRows(sheetDays, ads), [sheetDays, ads]);

  const sheetColumns = useMemo<ColumnsType<SheetRow>>(
    () => [
      {
        title: 'Row',
        dataIndex: 'sheetRow',
        key: 'sheetRow',
        fixed: 'left',
        width: 76,
        render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{v}</span>,
      },
      {
        title: 'Metric',
        dataIndex: 'label',
        key: 'label',
        fixed: 'left',
        width: 280,
        render: (label: string, r) =>
          r.hint ? (
            <span>
              {label}{' '}
              <Tooltip title={r.hint}>
                <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
              </Tooltip>
            </span>
          ) : (
            label
          ),
      },
      ...sheetDays.map((d, i) => ({
        title: (
          <span style={{ whiteSpace: 'nowrap' }}>
            {dayjs(d.day).format('DD MMM')}{' '}
            <Text type="secondary" style={{ fontSize: 11 }}>
              {dayjs(d.day).format('ddd')}
            </Text>
          </span>
        ),
        key: d.day,
        align: 'right' as const,
        width: 112,
        render: (_: unknown, r: SheetRow) => (
          <span style={{ whiteSpace: 'nowrap' }}>{r.display[i]}</span>
        ),
      })),
    ],
    [sheetDays]
  );

  const totals = useMemo(
    () =>
      funnel.reduce(
        (acc, r) => ({
          firstOpens: acc.firstOpens + r.firstOpens,
          tutorialBegin: acc.tutorialBegin + r.tutorialBegin,
          tutorialComplete: acc.tutorialComplete + r.tutorialComplete,
          firstMatch: acc.firstMatch + r.firstMatch,
          d1Retained: acc.d1Retained + r.d1Retained,
        }),
        {
          firstOpens: 0,
          tutorialBegin: 0,
          tutorialComplete: 0,
          firstMatch: 0,
          d1Retained: 0,
        }
      ),
    [funnel]
  );

  // How much of the abandonment picture is the sweeper rather than players.
  const abandonTotals = useMemo(() => {
    const total = abandon.reduce((s, r) => s + r.matches, 0);
    const timeout = abandon
      .filter((r) => r.abandonReason === 'timeout')
      .reduce((s, r) => s + r.matches, 0);
    const byBucket = BUCKET_ORDER.map((b) => ({
      bucket: b,
      matches: abandon.filter((r) => r.overBucket === b).reduce((s, r) => s + r.matches, 0),
    })).filter((r) => r.matches > 0);
    return { total, timeout, byBucket };
  }, [abandon]);

  const funnelSteps = [
    { label: 'First open', value: totals.firstOpens },
    { label: 'Tutorial started', value: totals.tutorialBegin },
    { label: 'Tutorial complete', value: totals.tutorialComplete },
    { label: 'First match', value: totals.firstMatch },
    { label: 'D1 retained', value: totals.d1Retained },
  ];

  const ftueColumns: ColumnsType<FtueRow> = [
    { title: 'Step', dataIndex: 'step', key: 'step' },
    {
      title: 'Entered',
      dataIndex: 'entered',
      key: 'entered',
      sorter: (a, b) => a.entered - b.entered,
    },
    {
      title: 'Median time in previous step',
      dataIndex: 'medianPreviousStepSeconds',
      key: 'median',
      render: (s: number) => (s >= 60 ? `${Math.round(s / 60)}m` : `${s}s`),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <RangePicker
          value={range}
          onChange={(v) =>
            v?.[0] && v?.[1] && isWithinReportCap(v[0], v[1]) && setRange([v[0], v[1]])
          }
          disabledDate={disabledBeyondReportCap}
          allowClear={false}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          At most {REPORT_MAX_DAYS} days
        </Text>
      </Space>

      {error && <Alert type="warning" message={error} showIcon style={{ marginBottom: 16 }} />}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card
            loading={loading}
            title={
              <span>
                Acquisition funnel{' '}
                <Tooltip title="D1 is only meaningful for cohorts formed after daily activity tracking started collecting. Earlier days read 0 because there is nothing to join against, not because nobody returned.">
                  <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                </Tooltip>
              </span>
            }
          >
            {funnelSteps.map((s) => (
              <div key={s.label} style={{ marginBottom: 12 }}>
                <Text>
                  {s.label} — <strong>{s.value.toLocaleString('en-IN')}</strong>{' '}
                  <Text type="secondary">({pct(s.value, totals.firstOpens)}% of first opens)</Text>
                </Text>
                <Progress percent={pct(s.value, totals.firstOpens)} showInfo={false} size="small" />
              </div>
            ))}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            loading={loading}
            title={
              <span>
                Abandonment by point in match{' '}
                <Tooltip title="Buckets are a fraction of each match's own over count, so 3-over and 20-over matches compare. Read the timeout share first — a large one means the client is not calling /abandon, so this shows where the sweeper found matches, not where players quit.">
                  <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                </Tooltip>
              </span>
            }
          >
            {abandonTotals.total === 0 ? (
              <Text type="secondary">No abandons in this range.</Text>
            ) : (
              <>
                <div style={{ marginBottom: 12 }}>
                  <Tag color={abandonTotals.timeout > abandonTotals.total / 2 ? 'red' : 'default'}>
                    {pct(abandonTotals.timeout, abandonTotals.total)}% closed by sweeper
                  </Tag>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {abandonTotals.timeout > abandonTotals.total / 2
                      ? ' — most abandons never called /abandon, so the distribution below is unreliable'
                      : ' — the rest were explicit quits'}
                  </Text>
                </div>
                {abandonTotals.byBucket.map((b) => (
                  <div key={b.bucket} style={{ marginBottom: 10 }}>
                    <Text>
                      {b.bucket} through the match —{' '}
                      <strong>{b.matches.toLocaleString('en-IN')}</strong>
                    </Text>
                    <Progress
                      percent={pct(b.matches, abandonTotals.total)}
                      showInfo={false}
                      size="small"
                      strokeColor={b.bucket === 'unknown' ? '#d9d9d9' : undefined}
                    />
                  </div>
                ))}
              </>
            )}
          </Card>
        </Col>

        <Col xs={24}>
          <Card
            loading={loading}
            title={
              <span>
                FTUE drop-off{' '}
                <Tooltip title="Reads the append-only step history, so re-running an old window returns the same numbers. Time in the previous step is the actionable half — a step everyone clears in seconds and one where people idle for minutes before leaving are different problems.">
                  <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                </Tooltip>
              </span>
            }
          >
            <Table
              rowKey="step"
              size="small"
              pagination={false}
              dataSource={ftue}
              columns={ftueColumns}
              locale={{
                emptyText:
                  'No step transitions recorded yet — this only collects from when FTUE history shipped.',
              }}
            />
          </Card>
        </Col>

        <Col xs={24}>
          <Card
            loading={reportLoading}
            title={
              <span>
                Daily report (sheet view){' '}
                <Tooltip title="Laid out like the team's spreadsheet: one row per metric, one column per IST day, oldest on the left. The Row column is the sheet's row number; — means the sheet has no row for it yet.">
                  <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                </Tooltip>
              </span>
            }
            extra={
              <Button
                size="small"
                icon={<DownloadOutlined />}
                disabled={reportLoading || sheetDays.length === 0}
                onClick={() => downloadSheetCsv(sheetDays, sheetRows)}
              >
                Download CSV
              </Button>
            }
          >
            {sheetTooWide ? (
              <Alert
                type="info"
                showIcon
                message={`The sheet view covers at most ${SHEET_MAX_DAYS} days. Pick a shorter range to see it.`}
              />
            ) : reportError ? (
              <Alert type="warning" showIcon message={reportError} />
            ) : (
              <Table<SheetRow>
                rowKey="key"
                size="small"
                bordered
                pagination={false}
                dataSource={sheetDays.length > 0 ? sheetRows : []}
                columns={sheetColumns}
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: 'No days in this range.' }}
              />
            )}
            <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
              D0 rows (17–18, 24–25) count matches a day's new users started or completed on that
              same IST day. Rows 43–44 come from GA4 events in BigQuery (USD) and lag by up to a
              day. DAU, sessions and FTUE rows only have data from when that tracking started, so
              earlier days read 0 or —.
            </Text>
          </Card>
        </Col>
      </Row>

      <Title level={5} style={{ marginTop: 16, fontSize: 12, fontWeight: 400 }}>
        <Text type="secondary">
          Every card here reads tables that only began collecting recently. Ranges reaching further
          back than that will under-report rather than error.
        </Text>
      </Title>
    </div>
  );
};
